import React, { Fragment, useState } from "react"

interface DraggableFilterListProps<T> {
  filters: T[]
  onChange: (index: number, filter: T) => void
  onRemove: (index: number) => void
  onReorder: (from: number, to: number) => void
  renderRow: (args: {
    filter: T
    index: number
    onChange: (next: T) => void
    onRemove: () => void
  }) => React.ReactNode
  renderConnector?: (index: number) => React.ReactNode
  getKey?: (filter: T, index: number) => string
  ariaPrefix: string
}

export function DraggableFilterList<T>({
  filters,
  onChange,
  onRemove,
  onReorder,
  renderRow,
  renderConnector,
  getKey,
  ariaPrefix,
}: DraggableFilterListProps<T>) {
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

  const handleDragStart = (
    event: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => {
    event.dataTransfer.effectAllowed = "move"
    setDragIndex(index)
  }

  const handleDragOver = (
    event: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
    if (dragOverIndex !== index) {
      setDragOverIndex(index)
    }
  }

  const handleDrop = (
    event: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => {
    event.preventDefault()
    if (dragIndex !== null && dragIndex !== index) {
      onReorder(dragIndex, index)
    }
    setDragIndex(null)
    setDragOverIndex(null)
  }

  const handleDragEnd = () => {
    setDragIndex(null)
    setDragOverIndex(null)
  }

  return (
    <div className="flex flex-col">
      {filters.map((filter, index) => {
        const isDragging = dragIndex === index
        const isDropTarget =
          dragOverIndex === index && dragIndex !== null && dragIndex !== index
        const key = getKey ? getKey(filter, index) : `${ariaPrefix}-${index}`
        return (
          <Fragment key={key}>
            <div
              draggable
              onDragStart={(event) => handleDragStart(event, index)}
              onDragOver={(event) => handleDragOver(event, index)}
              onDrop={(event) => handleDrop(event, index)}
              onDragEnd={handleDragEnd}
              className={`rounded-md py-1 transition-colors ${
                isDragging ? "opacity-50" : ""
              } ${isDropTarget ? "bg-surface-1" : ""}`}
            >
              {renderRow({
                filter,
                index,
                onChange: (next) => onChange(index, next),
                onRemove: () => onRemove(index),
              })}
            </div>
            {renderConnector && index < filters.length - 1 && (
              <div className="flex items-center gap-2 pl-6 pr-9 py-0.5">
                <div className="h-px flex-1 bg-surface-2" />
                {renderConnector(index)}
                <div className="h-px flex-1 bg-surface-2" />
              </div>
            )}
          </Fragment>
        )
      })}
    </div>
  )
}

export function reorder<T>(items: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= items.length) {
    return items
  }
  const next = [...items]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}
